import { useState } from 'react'
import { Alert, Button, Dropdown, Form, Input, Modal } from 'antd'
import { PlusOutlined, MoreOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { DataTable } from '@kolab/dms-base/components/ui'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { toast, notify, getFriendlyErrorMessage, optionalPhoneValidator } from '@kolab/dms-base/lib'
import {
  createDistributor,
  listDistributors,
  setDistributorStatus,
  softDeleteDistributor,
  updateDistributor,
} from '../api/distributor.api'
import { distributorKeys } from '../api/queryKeys'
import type { Distributor, DistributorStatus } from '../api/types'
import { StatusBadge } from '../components/common/StatusBadge'
import { Can, P, usePermissionStore } from '../rbac'
import { useCursorList } from '../hooks/useCursorList'
import { CursorListStateView } from '../components/common/QueryStateView'


type Mode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; distributor: Distributor }

export default function DistributorsPage() {
  const queryClient = useQueryClient()

  // Cursor-based infinite scroll — DataTable auto-fetches the next page when
  // the user scrolls near the bottom of the table body. All filter/sort state
  // (none yet on this page) should be passed via `params` so the query key
  // rebuilds and page 1 is fetched fresh.
  const { items, isLoading, isError, error, refetch, isFetchingNextPage, hasNextPage, fetchNextPage } = useCursorList<Distributor>({
    queryKey: distributorKeys.list({ pageSize: 20 }),
    queryFn: (params) => listDistributors(params),
    pageSize: 20,
  })

  const { hasPermission } = usePermissionStore()

  const [modal, setModal] = useState<Mode>({ kind: 'closed' })
  const [formError, setFormError] = useState<string | null>(null)

  function handleFormError(error: unknown, fallback: string) {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status?: number }).status
      if (status === 401 || status === 403 || (status && status >= 500)) return
    }
    setFormError(getFriendlyErrorMessage(error, fallback))
  }

  function openModal(mode: Mode) {
    setFormError(null)
    setModal(mode)
  }

  const createMut = useMutation({
    mutationFn: createDistributor,
    onSuccess: () => {
      toast.success('Distributor created')
      queryClient.invalidateQueries({ queryKey: distributorKeys.all })
      setModal({ kind: 'closed' })
    },
    onError: (error: unknown) => handleFormError(error, 'Failed to create distributor'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateDistributor(id, { name }),
    onSuccess: () => {
      toast.success('Distributor updated')
      queryClient.invalidateQueries({ queryKey: distributorKeys.all })
      setModal({ kind: 'closed' })
    },
    onError: (error: unknown) => handleFormError(error, 'Failed to update distributor'),
  })

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DistributorStatus }) =>
      setDistributorStatus(id, { status }),
    onSuccess: () => {
      toast.success('Status updated')
      queryClient.invalidateQueries({ queryKey: distributorKeys.all })
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || (status && status >= 500)) return
      }
      toast.error(getFriendlyErrorMessage(error, 'Failed to update distributor status'))
    },
  })

  const deleteMut = useMutation({
    mutationFn: softDeleteDistributor,
    onSuccess: () => {
      toast.success('Distributor deactivated')
      queryClient.invalidateQueries({ queryKey: distributorKeys.all })
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || (status && status >= 500)) return
      }
      notify.error({
        message: 'Deactivation failed',
        description: getFriendlyErrorMessage(error, 'Failed to deactivate distributor'),
      })
    },
  })

  const columns: ColumnsType<Distributor> = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'ID', dataIndex: 'id', key: 'id' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <StatusBadge status={s} />,
    },
    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt' },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const items = [
          hasPermission(P.tenants.distributor.update) && {
            key: 'edit',
            label: 'Edit',
            onClick: () => openModal({ kind: 'edit', distributor: record }),
          },
          hasPermission(P.tenants.distributor.suspend) && {
            key: 'activate',
            label: 'Set ACTIVE',
            onClick: () => statusMut.mutate({ id: record.id, status: 'ACTIVE' }),
          },
          hasPermission(P.tenants.distributor.suspend) && {
            key: 'suspend',
            label: 'Suspend',
            onClick: () => statusMut.mutate({ id: record.id, status: 'SUSPENDED' }),
          },
          hasPermission(P.tenants.distributor.delete) && {
            key: 'delete',
            label: 'Deactivate',
            danger: true,
            onClick: () => deleteMut.mutate(record.id),
          },
        ].filter(Boolean) as { key: string; label: string; danger?: boolean; onClick: () => void }[]

        if (items.length === 0) return null
        return (
          <Dropdown trigger={['click']} menu={{ items }} getPopupContainer={() => document.body}>
            <Button size="small" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]

  return (
    <PageLayout
      actions={
        <Can permission={P.tenants.distributor.create}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal({ kind: 'create' })}>
            New distributor
          </Button>
        </Can>
      }
    >
      <CursorListStateView isLoading={isLoading} isError={isError} error={error} refetch={refetch}>
        <DataTable<Distributor>
          rowKey="id"
          columns={columns}
          dataSource={items}
          pagination={false}
          infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        />
      </CursorListStateView>

      <DistributorFormModal
        mode={modal}
        onCancel={() => setModal({ kind: 'closed' })}
        onCreate={(values) => createMut.mutate(values)}
        onEdit={(name) => {
          if (modal.kind === 'edit') updateMut.mutate({ id: modal.distributor.id, name })
        }}
        submitting={createMut.isPending || updateMut.isPending}
        error={formError}
        onClearError={() => setFormError(null)}
      />
    </PageLayout>
  )
}

// Fields captured on distributor creation. On edit we only allow renaming —
// admin identity is snapshot at creation and never rewritten from the SPA.
type CreateFormValues = {
  name: string
  adminEmail: string
  adminFullName: string
  adminPhone?: string
}

function DistributorFormModal({
  mode,
  onCancel,
  onCreate,
  onEdit,
  submitting,
  error,
  onClearError,
}: {
  mode: Mode
  onCancel: () => void
  onCreate: (values: CreateFormValues) => void
  onEdit: (name: string) => void
  submitting: boolean
  error: string | null
  onClearError: () => void
}) {
  const [form] = Form.useForm<CreateFormValues>()
  const isCreate = mode.kind === 'create'
  const initial =
    mode.kind === 'edit'
      ? { name: mode.distributor.name }
      : { name: '', adminEmail: '', adminFullName: '', adminPhone: '' }

  return (
    <Modal
      open={mode.kind !== 'closed'}
      title={isCreate ? 'New distributor' : 'Edit distributor'}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okButtonProps={{ loading: submitting }}
      destroyOnClose
    >
      {error && (
        <Alert type="error" message={error} closable onClose={onClearError} style={{ marginBottom: 16 }} />
      )}
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={initial}
        onFinish={(values) => {
          if (isCreate) onCreate(values)
          else onEdit(values.name)
        }}
      >
        <Form.Item
          name="name"
          label="Distributor name"
          rules={[{ required: true, message: 'Name is required' }, { max: 200, message: 'Name must not exceed 200 characters' }]}
        >
          <Input placeholder="e.g. ACME Corp" />
        </Form.Item>

        {isCreate && (
          <>
            <div style={{ margin: '8px 0 12px', color: '#6b7280', fontSize: 12 }}>
              The primary administrative user is created in IAM asynchronously.
              A temporary password will be emailed to the address below; the user
              is forced to change it on first login.
            </div>
            <Form.Item
              name="adminFullName"
              label="Admin full name"
              rules={[{ required: true, message: 'Admin full name is required' }, { max: 200, message: 'Name must not exceed 200 characters' }]}
            >
              <Input placeholder="e.g. Alice Doe" />
            </Form.Item>
            <Form.Item
              name="adminEmail"
              label="Admin email"
              rules={[
                { required: true, message: 'Admin email is required' },
                { type: 'email', message: 'Enter a valid email address' },
              ]}
            >
              <Input placeholder="admin@example.com" />
            </Form.Item>
            <Form.Item
              name="adminPhone"
              label="Admin phone (optional)"
              rules={[{ validator: optionalPhoneValidator }]}
            >
              <Input placeholder="+1 555 0100" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  )
}

export { DistributorsPage }
